<script lang="ts">
	//Hooks
	import { beforeUpdate, onMount } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { readDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	//Helpers
	import { get_local_ip, get_ssid, get_ip, is_gen_2 } from '../../../helpers/interface_helpers';
	import { getActions } from '../../DevicesSettings/WebHooks/WebHooksHelpers/get_actions';
	import { filterDevice } from '../../Lists/list-helpers';
	//Constants
	import { DOOR_SENSORS, NEW_HOOKS, TEMPERATURE_SENSORS } from '../../../constants';
	//Utilities
	import { handleSingleSensorSaveActions } from './../helpers';
	//Components
	import DevicesSelectHolder from './Components/DevicesSelectHolder.svelte';

	import { Alert, Text, Stack, Input, Collapse, Container } from '@Shelly/shelly-design-system';
	import { capFirstChar } from '../../../../src/helpers/utilities';
	import NoSearchResults from 'src/components/Lists/NoSearchResults.svelte';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	export let editableObject;
	let deviceActions = Object.assign({}, { ...editableObject });

	const windowReport = deviceActions?.condition === deviceActions?.options?.[0]; //reactive because component is loaded inside modal
	let device = deviceActions.device;
	let actions;
	let filteredDevices = [];
	let ipAddresses = [];
	let list = [];
	let _devices = Object.entries($devices);
	let isThermostat = deviceActions.mode === 'thermostat';
	const allowedCategories = ['relay', 'light'];
	let deviceFilter = '';
	let roomsExpanded = {};
	let windowClientWidth: number;

	let selectedCategoryValue = 'relay';

	const description = !isThermostat
		? $format('quick_actions_description') ||
			'Select up to 5 devices which will respond to motion action:'
		: windowReport
			? $format('quick_actions_description_window')
			: $format('quick_actions_description_single') ||
				'Select which sensor will report temperature to your valve:';

	/**
	 * @function pushToRooms arranges rooms devices suitable for the collapse holder
	 * @param devices all devices
	 * @param rooms all rooms
	 * @param _filteredDevices devices by category
	 * @param component component to be loaded for now it is only devices select holder
	 * @returns collapse friendly list with filtered devices and collapse props
	 */
	const pushToRooms = (devices, rooms, _filteredDevices, component) => {
		const list = [];
		let devicesInRoom = [];
		const hostStatus = readDeviceStatus(device.id);
		if (!hostStatus) {
			return list;
		}

		const hostSettings = readDeviceSettings(device.id);
		const ssid = get_ssid(hostSettings, hostStatus, devices[device.id]);

		for (let room in rooms) {
			devicesInRoom = [];
			_filteredDevices.map((_device) => {
				const deviceSettings = readDeviceSettings(_device.id);
				const deviceStatus = readDeviceStatus(_device.id);
				const device_ssid = get_ssid(deviceSettings, deviceStatus, devices[_device.id]);
				if (
					rooms[room].id === _device.room_id &&
					!_device.shared &&
					ssid === device_ssid &&
					filterDevice(_device, deviceFilter.toLowerCase())
				) {
					devicesInRoom.push(_device);
				}
			});

			if (devicesInRoom.length > 0) {
				list.push({
					roomID: room,
					title: rooms[room].name,
					type: 'non_format_title',
					component: component,
					props: {
						filteredDevices: devicesInRoom,
						id: device.id,
					},
					open: list.length,
					ex: true,
					sep_container: true,
				});
			}
		}

		return list;
	};

	/**
	 * @function filterDevices will arrange devices based on category.
	 * - If we came from trv device and window report is selected, deices which will be returned are only door window sensors.
	 * - If we came from trv device and temperature report is selected, deices which will be returned are all temperature sensors.
	 * - If we came from motion sensor returned devices are filtered by category selection (light and relay, for now).
	 * - also we see devices which are in the same network as the initial device, because actions will include local ip address.
	 */
	const filterDevices = async () => {
		filteredDevices = [];
		_devices.forEach((_device: any) => {
			const _category = _device[1].category;

			if ($devices[device.id].category !== 'thermostat') {
				if (allowedCategories.includes(_category) && _category === deviceActions.type) {
					filteredDevices.push({ id: _device[0], ..._device[1] });
				}
			} else {
				if (windowReport && _category === 'sensor') {
					if (
						DOOR_SENSORS.includes(_device[1].type) &&
						!filteredDevices.some((x) => x.id === _device[0])
					) {
						filteredDevices.push({ id: _device[0], ..._device[1] });
					} else if (DOOR_SENSORS.includes(_device[1].type)) {
						filteredDevices.filter((x) => x.id === _device[0]);
					}
				} else if (!windowReport && _category === 'sensor') {
					if (
						TEMPERATURE_SENSORS.includes(_device[1].type) &&
						!filteredDevices.some((x) => x.id === _device[0])
					) {
						filteredDevices.push({ id: _device[0], ..._device[1] });
					}
				}
			}
		});

		list = pushToRooms($devices, $rooms, filteredDevices, DevicesSelectHolder);

		// console.warn('deviceActions', $deviceActions);
		actions =
			typeof deviceActions?.[device.id] === 'undefined'
				? await getActions(device, get_local_ip(device.id))
				: deviceActions[device.id];
		if (actions?.isok) {
			delete actions.isok;
		}
		if (typeof deviceActions[device.id] !== 'undefined') {
			deviceActions[device.id] = { ...deviceActions[device.id], ...actions };
		}
	};

	/**
	 * @function handleSave we are getting actions based on first selected device. This case is while we are arriving from trv device. After that saving actions via request.
	 */
	const handleSave = async () => {
		let type: string;

		if (NEW_HOOKS.includes($devices[deviceActions?.selectedDevices[0]]?.type)) {
			type = 'newHooks';
		} else if ($devices[deviceActions?.selectedDevices[0]]?.type === 'SHMOS-02') {
			type = 'newActions';
		}

		await handleSingleSensorSaveActions(
			device,
			$devices,
			deviceActions.selectedDevices,
			deviceActions,
			windowReport,
			'',
			type,
		);
		deviceActions.loading = false;
		deviceActions.blindTime = undefined;
		deviceActions.callback = undefined;
		deviceActions.selectedDevices = [];
		deviceActions.category = undefined;
		deviceActions.ips = [];
		deviceActions.selectedAction = undefined;
	};

	/**
	 * @function updateDevices here we are collecting ip addresses based on
	 * @param e which is returned event from devices select holder.
	 */
	const updateDevices = async (e) => {
		deviceActions.callback = '';
		deviceActions.selectedDevices = e.detail.selectedDevices;

		if (isThermostat) {
			actions = await getActions(
				$devices[deviceActions.selectedDevices[0]],
				get_local_ip(deviceActions.selectedDevices[0]),
			);
			if (actions?.isok) {
				delete actions.isok;
			}
			deviceActions[deviceActions.selectedDevices[0]] = { ...actions };
			deviceActions.callback = handleSave;
		}

		if (!isThermostat) {
			ipAddresses = [];
			deviceActions.selectedDevices.forEach((id) => {
				ipAddresses.push({
					id: id,
					ip: get_ip(readDeviceStatus(id)),
					category: $devices[id].category,
				});
			});
			deviceActions.ips = ipAddresses;
			deviceActions[device.id] = {
				...deviceActions[device.id],
			};
		} else if (deviceActions.category === 'sensor') {
			if (deviceActions.selectedDevices.length > 1) {
				deviceActions.selectedDevices.pop(); //need to remain only one device
			}
			actions = undefined;

			if (
				typeof deviceActions?.[deviceActions.selectedDevices[0]] !== 'undefined' &&
				Object.keys(deviceActions?.[deviceActions.selectedDevices[0]]).length > 0
			) {
				actions = deviceActions?.[deviceActions.selectedDevices[0]];
			} else {
				if (NEW_HOOKS.includes($devices[deviceActions.selectedDevices[0]]?.type)) {
					actions = [];
				} else {
					if (typeof deviceActions.selectedDevices[0] !== 'undefined') {
						// deviceActions.loading = true;
						actions = await getActions(
							$devices[deviceActions.selectedDevices[0]],
							get_ip($devices[deviceActions.selectedDevices[0]]),
							// ).finally(() => {
							// 	deviceActions.loading = false;
							// 	editableObject.disabled = false;
							// }
						);
						if (actions?.isok) {
							delete actions.isok;
						}
					}
				}
			}

			if (
				(typeof actions !== 'undefined' && Object.keys(actions).length > 0) ||
				(Array.isArray(actions) && is_gen_2($devices[deviceActions.selectedDevices[0]])) //unlocking save btn for empty array actions
			) {
				deviceActions[deviceActions.selectedDevices[0]] = { ...actions };
			} else {
				if (
					typeof deviceActions.selectedDevices[0] !== 'undefined' &&
					deviceActions.selectedDevices[0].length > 0
				) {
					// deviceActions.loading = true;
					actions = await getActions(
						$devices[deviceActions.selectedDevices[0]],
						get_local_ip($devices[deviceActions.selectedDevices[0]]),
					);
					if (actions?.isok) {
						delete actions.isok;
					}
				}
			}
		}
		// deviceActions.loading = false;
		editableObject = deviceActions;
	};

	onMount(() => {
		filterDevices();
	});

	beforeUpdate(() => {
		if (typeof deviceActions[deviceActions.selectedDevices[0]] !== 'undefined') {
			editableObject.disabled = false;
		}
	});

	// changes category and refilter devices based on
	$: if (selectedCategoryValue !== deviceActions.type) {
		selectedCategoryValue = deviceActions.type;
		filterDevices();
	}

	// refilter devices if search
	$: if (deviceFilter !== null) {
		filterDevices();
	}

	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

<Container noScroll padding="0">
	<Alert
		class={classMap({
			'mb-l': !isMobile,
			'mb-m': isMobile,
		})}
		heading={description}
	/>

	<Stack
		gap="s"
		class="mb-l"
		direction="column"
		directionMobile="row"
		align="between"
		alignMobile="none"
	>
		<Text element="h2" noWrap class="align-self-center">
			{$format('label_select_device')}:
			<span class="text-primary">{deviceActions.selectedDevices.length}</span>
		</Text>
		<Input
			type="text"
			label={capFirstChar($format('search'))}
			icon="fas fa-search"
			class="width-lg-l width-sm-full"
			bind:value={deviceFilter}
			id="input-je7tuaq"
			aria-labelledby="input-je7tuaq"
		/>
	</Stack>

	{#if list.length < 1}
		<NoSearchResults search={deviceFilter} />
	{:else}
		<Stack gap="s">
			{#each list as item}
				<Collapse
					id="collapse-nrevhqq"
					title={item.title}
					expanded={roomsExpanded[item.roomID] ||
						item.props.filteredDevices
							.map((device) => device.id)
							.includes(deviceActions.selectedDevices[0])}
					on:click={() => {
						roomsExpanded[item.roomID] = !roomsExpanded[item.roomID];
					}}
				>
					<DevicesSelectHolder
						{isThermostat}
						filteredDevices={item.props.filteredDevices}
						bind:deviceActions
						on:update={updateDevices}
					/>
				</Collapse>
			{/each}
		</Stack>
	{/if}
</Container>
